import React, { Fragment } from 'react';
import { useDispatch } from 'react-redux';
import { getS7CommsData } from '../../redux/actions/otActions';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';

import Content from '../../components/Content';
import OTDataTable from '../../components/OTDataTable';
import Loader from '../../components/Loader';
import NetworkError from '../../components/Error';

import S7CommsStyles from './S7Comms.module.css';

const FRONTEND_S7COMMS_ERROR_CODES = {
	INITIAL_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
};

const buildS7CommsSupportError = (errorCode, pageInfo, sourceMessage) =>
	formatFrontendSupportError({ errorCode, pageInfo, sourceMessage });

const S7CommsScreen = () => {
	const dispatch = useDispatch();
	const [ dataLoading, setDataLoading ] = React.useState(false);

	const [ s7CommsData, setS7CommsData ] = React.useState({});
	const [ supportErrorMessage, setSupportErrorMessage ] = React.useState('');
	const [ copiedSupportError, setCopiedSupportError ] = React.useState(false);

	const setS7CommsSupportError = React.useCallback((errorCode, pageInfo, sourceMessage) => {
		setSupportErrorMessage(buildS7CommsSupportError(errorCode, pageInfo, sourceMessage));
		setCopiedSupportError(false);
	}, []);

	const clearS7CommsSupportError = React.useCallback((force = false) => {
		if (!force) return;
		setSupportErrorMessage('');
		setCopiedSupportError(false);
	}, []);

	const handleCopySupportError = React.useCallback(async () => {
		if (!supportErrorMessage) return;
		try {
			await navigator.clipboard.writeText(supportErrorMessage);
			setCopiedSupportError(true);
			setTimeout(() => setCopiedSupportError(false), 2000);
		} catch (error) {
			setCopiedSupportError(false);
		}
	}, [ supportErrorMessage ]);

	React.useEffect(
		() => {
			const getData = async () => {
				setDataLoading(true);
				try {
					const data = await dispatch(getS7CommsData());
					if (
						data &&
						data.status &&
						Array.isArray(data.S7CommsRequestCols) &&
						Array.isArray(data.S7CommsRequestRows) &&
						Array.isArray(data.S7CommsResponseCols) &&
						Array.isArray(data.S7CommsResponseRows)
					) {
						setS7CommsData(data);
						clearS7CommsSupportError(true);
					} else {
						setS7CommsData({});
						setS7CommsSupportError(
							FRONTEND_S7COMMS_ERROR_CODES.INITIAL_LOAD_FAILED,
							'S7 Comms Page / Initial Data Load Failed',
							data?.msg || (typeof data === 'string' ? data : 'S7 Comms request did not return success')
						);
					}
				} catch (error) {
					setS7CommsData({});
					setS7CommsSupportError(
						FRONTEND_S7COMMS_ERROR_CODES.INITIAL_LOAD_FAILED,
						'S7 Comms Page / Initial Data Request Error',
						error?.message || String(error)
					);
				} finally {
					setDataLoading(false);
				}
			};

			getData();
		},
		[ clearS7CommsSupportError, dispatch, setS7CommsSupportError ]
	);

	return (
		<Content currentMenu='OT' headerTitle='S7 Comms'>
			{dataLoading ? (
				<Loader />
			) : s7CommsData && Object.keys(s7CommsData).length === 0 ? (
				<div>
					<NetworkError />
					{supportErrorMessage && (
						<div className={S7CommsStyles.supportErrorCard}>
							<strong>Support Error:</strong>
							<code className={S7CommsStyles.supportErrorCode}>{supportErrorMessage}</code>
							<div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
								<button
									type='button'
									className={S7CommsStyles.copySupportErrorButton}
									onClick={handleCopySupportError}
								>
									{copiedSupportError ? 'Copied' : 'Copy Error Details'}
								</button>
								<button
									type='button'
									className={S7CommsStyles.copySupportErrorButton}
									onClick={() => clearS7CommsSupportError(true)}
								>
									Clear
								</button>
							</div>
						</div>
					)}
				</div>
			) : (
				<Fragment>
					{supportErrorMessage && (
						<div className={S7CommsStyles.supportErrorBanner}>
							<div className={S7CommsStyles.supportErrorBannerText}>
								<strong>Support Error:</strong>
								<code className={S7CommsStyles.supportErrorCode}>{supportErrorMessage}</code>
							</div>
							<div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
								<button
									type='button'
									className={S7CommsStyles.copySupportErrorButton}
									onClick={handleCopySupportError}
								>
									{copiedSupportError ? 'Copied' : 'Copy Error Details'}
								</button>
								<button
									type='button'
									className={S7CommsStyles.copySupportErrorButton}
									onClick={() => clearS7CommsSupportError(true)}
								>
									Clear
								</button>
							</div>
						</div>
					)}
					<div className={S7CommsStyles.tables}>
						<div className={S7CommsStyles.table}>
							<OTDataTable
								title='S7 Comms Requests'
								searchPlaceholder='Search Here'
								cols={s7CommsData.S7CommsRequestCols}
								rows={s7CommsData.S7CommsRequestRows}
							/>
						</div>
						<div className={S7CommsStyles.table}>
							<OTDataTable
								title='S7 Comms Responses'
								searchPlaceholder='Search Here'
								cols={s7CommsData.S7CommsResponseCols}
								rows={s7CommsData.S7CommsResponseRows}
							/>
						</div>
					</div>
				</Fragment>
			)}
		</Content>
	);
};

export default S7CommsScreen;
